import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Empty, Pagination, Select, Space, Tag, Typography, message } from 'antd';
import API_CONFIG from '../../config/api';
import { useEcommercePermissions } from '../../hooks/useEcommercePermissions';
import { ecommerceRequestHeaders } from '../ecommerceWorkspaceUtils';

export type Fulfillment = { id: number; order_id: number; order_number?: string; warehouse_name: string; status: string;
  payment_method?: string; payment_status?: string; customer_phone?: string; shipping_address?: any;
  items: { product_id: number; product_name: string; quantity: number; unit_price: number }[] };
const labels: Record<string, string> = { pending: 'En attente', confirmed: 'Confirmée', processing: 'En préparation', shipped: 'Expédiée', delivered: 'Livrée', cancelled: 'Annulée' };
const nextStatuses: Record<string, string[]> = { pending: ['confirmed', 'processing', 'shipped', 'delivered'], confirmed: ['processing', 'shipped', 'delivered'], processing: ['shipped', 'delivered'], shipped: ['delivered'] };
export function FulfillmentSummary({ groups = [] }: { groups?: Fulfillment[] }) {
  if (!groups.length) return null;
  return <Space direction="vertical" style={{ width: '100%', marginBlock: 16 }}>
    <Typography.Text strong>Préparations par établissement</Typography.Text>
    {groups.map((group) => <Card key={group.id} size="small" title={group.warehouse_name} extra={<Tag>{labels[group.status] || group.status}</Tag>}>
      {group.items.map((item) => <div key={item.product_id}>{item.product_name} × {Number(item.quantity)}</div>)}
    </Card>)}
  </Space>;
}

export type FulfillmentQueueSummary = { loading: boolean; total: number; error: string; status: string };
export default function FulfillmentQueue({ warehouseId, onUpdated, refreshKey = 0, onSummaryChange }: {
  warehouseId: number; onUpdated?: () => void; refreshKey?: number;
  onSummaryChange?: (summary: FulfillmentQueueSummary) => void;
}) {
  const { canProcessOrders } = useEcommercePermissions();
  const [rows, setRows] = useState<Fulfillment[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState<number | null>(null);
  const [revision, setRevision] = useState(0);
  const [selected, setSelected] = useState<Record<number, string>>({});
  useEffect(() => { setPage(1); setRows([]); setTotal(0); setSelected({}); }, [warehouseId]);
  useEffect(() => {
    onSummaryChange?.({ loading, total, error, status: labels[status] || '' });
  }, [loading, total, error, status, onSummaryChange]);
  useEffect(() => {
    if (!warehouseId) return;
    const controller = new AbortController();
    setLoading(true); setError('');
    fetch(`${API_CONFIG.BASE_URL}/api/ecommerce/orders/fulfillments?warehouse_id=${warehouseId}&page=${page}&status=${status}`, {
      headers: ecommerceRequestHeaders(), signal: controller.signal,
    }).then(async (response) => {
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Chargement impossible.');
      if (!controller.signal.aborted) { setRows(data.fulfillments || []); setTotal(Number(data.total) || 0); }
    }).catch((e) => { if (!controller.signal.aborted) setError(e.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [warehouseId, page, status, revision, refreshKey]);
  const update = useCallback(async (row: Fulfillment, next: string) => {
    setSaving(row.id);
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/ecommerce/orders/${row.order_id}/fulfillments/${row.id}/status`, {
        method: 'PUT', headers: ecommerceRequestHeaders(), body: JSON.stringify({ status: next }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Mise à jour impossible.');
      setSelected((values) => { const nextValues = { ...values }; delete nextValues[row.id]; return nextValues; });
      message.success('Préparation mise à jour.'); setRevision((r) => r + 1); onUpdated?.();
    } catch (e: any) { message.error(e.message); } finally { setSaving(null); }
  }, [onUpdated]);
  return <Card title="Préparations de l'établissement" size="small" style={{ marginBottom: 20 }} loading={loading && !rows.length}
    extra={<Button size="small" onClick={() => setRevision((r) => r + 1)}>Actualiser</Button>}>
    <Space direction="vertical" style={{ width: '100%' }}>
      <Typography.Text type="secondary">Articles des commandes communes à préparer dans cet établissement. L’encaissement est suivi sur la commande principale.</Typography.Text>
      <Select aria-label="Statut des préparations" value={status} style={{ minWidth: 180 }} onChange={(v) => { setStatus(v); setPage(1); }} options={[{ value: '', label: 'Tous les statuts' }, ...Object.entries(labels).map(([value, label]) => ({ value, label }))]} />
      {error && <Alert type="error" message={error} />}
      {!loading && !rows.length && !error && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune préparation commune" />}
      {rows.map((row) => {
        let address: any = row.shipping_address;
        try { if (typeof address === 'string') address = JSON.parse(address); } catch { address = null; }
        const blocked = row.payment_method === 'yengapay' && row.payment_status !== 'paid';
        const options = nextStatuses[row.status] || [];
        return <Card key={row.id} size="small" title={row.order_number} extra={<Tag>{labels[row.status] || row.status}</Tag>}>
          {row.items.map((item) => <div key={item.product_id}>{item.product_name} × {Number(item.quantity)}</div>)}
          <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>{row.customer_phone} · {address?.formatted_address || address?.street_address || address?.address || ''}</Typography.Paragraph>
          {blocked && <Typography.Paragraph type="secondary">En attente de confirmation du paiement en ligne.</Typography.Paragraph>}
          {canProcessOrders() && options.length > 0 && <Space wrap>
            <Select aria-label={`Nouveau statut ${row.order_number}`} value={selected[row.id] || options[0]} style={{ minWidth: 165 }} disabled={blocked || saving !== null}
              onChange={(value) => setSelected((values) => ({ ...values, [row.id]: value }))} options={options.map((value) => ({ value, label: labels[value] }))} />
            <Button type="primary" loading={saving === row.id} disabled={blocked || saving !== null} onClick={() => update(row, selected[row.id] || options[0])}>Valider</Button>
          </Space>}
        </Card>;
      })}
      {total > 20 && <Pagination current={page} total={total} pageSize={20} showSizeChanger={false} onChange={setPage} size="small" />}
    </Space>
  </Card>;
}
